import { Container, Assets, Texture } from "pixi.js";

import type { IStudioWidget } from "../../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import type { IStudioWidgetSocials } from "../../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import IntegrationsService from "../../../../../infrastructure/Services/IntegrationsService";

import type { IntegrationUser } from "../../../../../infrastructure/Repositories/Integrations/BaseIntegrationsRepository.interface";

import { ThemeFactory } from "./Themes/ThemeFactory";

import { ISocialIntegrationData } from "./Themes/ISocialsThemeRenderer";

import { getSocialDesign } from "../../../../../data/socialDesignsCatalog";

import { SocialDesignRenderer } from "./SocialDesignRenderer";

import kickLogo from "../../../../../assets/images/streamings-icons/kick-logo.png";

import restreamLogo from "../../../../../assets/images/streamings-icons/restream-logo.png";

import twitchLogo from "../../../../../assets/images/streamings-icons/twitch-logo.png";

import youtubeLogo from "../../../../../assets/images/streamings-icons/youtube-logo.png";

// Re-defining platform data locally to avoid circular dependencies with React hooks or UI constants

const SOCIAL_PLATFORMS_DATA = [
  { value: "twitch", color: "#9146ff" },

  { value: "youtube", color: "#ff0000" },

  { value: "kick", color: "#000000" },

  { value: "restream", color: "#ffffff" },
];

export class SocialsWidgetRenderer {
  private integrationsService: IntegrationsService;

  private integrationCache: Record<string, IntegrationUser | null> = {};

  private textures: Record<string, Texture> = {};

  private isLoading = false;

  private loadingPromise: Promise<void> | null = null;

  private dataFetched = false;

  // One SocialDesignRenderer per widget id, when options.designId is set.

  private designRenderers: Record<string, SocialDesignRenderer> = {};

  constructor() {
    this.integrationsService = new IntegrationsService();
  }

  /** Tear down (and forget) a widget id's active design renderer, if any. */

  private cleanupDesign(widgetId: string) {
    const existing = this.designRenderers[widgetId];

    if (existing) {
      existing.destroy();

      delete this.designRenderers[widgetId];
    }
  }

  /**
   * Derive the @handle to bake into a design card from the connected
   * integrations (first selected platform with a user), falling back to the
   * catalog sample. Reuses the same integration cache the legacy themes use.
   */

  private deriveHandle(selectedPlatforms: string[]): string {
    for (const platform of selectedPlatforms) {
      const data = this.integrationCache[platform];

      const name = data?.user?.username || data?.user?.displayName;

      if (name) {
        return name.startsWith("@") ? name : `@${name}`;
      }
    }

    return "@eriol.streams";
  }

  private async loadAssets() {
    if (this.loadingPromise) return this.loadingPromise;

    this.loadingPromise = (async () => {
      const [twitch, youtube, kick, restream] = await Promise.all([
        Assets.load(twitchLogo),

        Assets.load(youtubeLogo),

        Assets.load(kickLogo),

        Assets.load(restreamLogo),
      ]);

      this.textures = {
        twitch,

        youtube,

        kick,

        restream,
      };
    })();

    return this.loadingPromise;
  }

  private async fetchData(selectedPlatforms: string[]) {
    if (this.dataFetched) return;

    // Only fetch needed platforms

    const promises = selectedPlatforms.map(async (platform) => {
      if (this.integrationCache[platform]) return;

      try {
        const data =
          await this.integrationsService.getUserIntegrations(platform);

        this.integrationCache[platform] = data;
      } catch (e) {
        console.error(`Failed to fetch ${platform} data`, e);

        this.integrationCache[platform] = null;
      }
    });

    await Promise.all(promises);

    this.dataFetched = true;
  }

  async render(
    widget: IStudioWidget,

    container: Container,

    destination: { x: number; y: number; width: number; height: number },
  ): Promise<void> {
    const socialWidget = widget as IStudioWidgetSocials;

    const options = socialWidget.options || {};

    const selectedPlatforms = options.selectedIntegrations || [];

    // Tear down whatever this widget id rendered last time (the factory hands us

    // a fresh container each render but destroys the old one WITHOUT its child

    // textures, so the design renderer frees its own texture here).

    this.cleanupDesign(widget.id);

    // ── Design-store path ──────────────────────────────────────────────────

    // When `options.designId` resolves to a catalog design, render that design's

    // card SVG (with the @handle baked in) as a sprite and skip the legacy

    // theme. Awaited so the texture is ready before the factory cacheAsTexture's

    // the static SOCIALS container.

    const design = getSocialDesign(options.designId);

    if (design) {
      // Best-effort handle fetch (reuses the legacy integration cache).

      try {
        await this.fetchData(selectedPlatforms);
      } catch {
        /* fall through with the sample handle */
      }

      const handle = this.deriveHandle(selectedPlatforms);

      const designRenderer = new SocialDesignRenderer(design);

      this.designRenderers[widget.id] = designRenderer;

      await designRenderer.render(container, destination, handle);

      return;
    }

    // 1. Background Mask/Rect

    // Even if transparent, good to have bounds? No, usually widgets are transparent.

    // But for debug/placeholder check:

    // container.removeChildren(); // Already handled by Factory clearing?

    // Factory calls removeChildren, so we start clean.

    // 2. Load Assets & Data

    if (!this.textures.twitch || !this.dataFetched) {
      if (!this.isLoading) {
        this.isLoading = true;

        await Promise.all([
          this.loadAssets(),

          this.fetchData(selectedPlatforms),
        ]);

        this.isLoading = false;

        // Trigger re-render... usually handled by frame loop if we are dirty?

        // But this is a one-shot render call.

        // We might need to call render again?

        // The Parent `WidgetsFactory` calls render.

        // If we await here, it blocks? `render` is async in Factory now?

        // WidgetsFactory: `await this.renderWidgetContent`. YES.
      } else {
        // Already loading, wait?

        await this.loadingPromise;
      }
    }

    // Prepare Data for Renderer

    const integrations: ISocialIntegrationData[] = [];

    selectedPlatforms.forEach((p) => {
      const apiData = this.integrationCache[p];

      const meta = SOCIAL_PLATFORMS_DATA.find((x) => x.value === p);

      // Only add if we have user data (isConnected behaviour)

      if (apiData && apiData.user) {
        integrations.push({
          platform: p,

          user: apiData.user,

          color: meta?.color || "#fff",

          isConnected: true,
        });
      }
    });

    if (integrations.length === 0) {
      // Nothing to show

      return;
    }

    // 3. Render Theme

    const theme = options.theme || "theme1";

    const renderer = ThemeFactory.getRenderer(theme);

    const scaleFactor = 2.01; // Global scale factor

    renderer({
      container,

      destination,

      integrations,

      widget: socialWidget,

      icons: this.textures,

      scaleFactor,
    });
  }

  public destroy() {
    for (const id of Object.keys(this.designRenderers)) {
      this.designRenderers[id].destroy();
    }

    this.designRenderers = {};
  }
}
